import { Eye, EyeOff, AlertCircle } from 'lucide-react'
import type { ProfileVisibility } from '@/types'
import { useTranslation } from 'react-i18next'

interface PrivacyControlsProps {
  visibility: ProfileVisibility
  onChange: (visibility: ProfileVisibility) => void
}

export default function PrivacyControls({
  visibility,
  onChange,
}: PrivacyControlsProps) {
  const { t } = useTranslation('profile')

  const sections = [
    { key: 'showPhoto', labelKey: 'profilePhoto' },
    { key: 'showContact', labelKey: 'contactInfo' },
    { key: 'showExperience', labelKey: 'experience' },
    { key: 'showEducation', labelKey: 'education' },
    { key: 'showSkills', labelKey: 'skills' },
    { key: 'showLanguages', labelKey: 'languages' },
    { key: 'showProjects', labelKey: 'projects' },
    { key: 'showDocuments', labelKey: 'documents' },
  ] as const

  const handleToggle = (key: keyof ProfileVisibility) => {
    onChange({
      ...visibility,
      [key]: !visibility[key],
    })
  }

  const handleShowAll = () => {
    onChange({
      showPhoto: true,
      showContact: true,
      showExperience: true,
      showEducation: true,
      showSkills: true,
      showLanguages: true,
      showProjects: true,
      showDocuments: true,
    })
  }

  const handleHideAll = () => {
    onChange({
      showPhoto: false,
      showContact: false,
      showExperience: false,
      showEducation: false,
      showSkills: false,
      showLanguages: false,
      showProjects: false,
      showDocuments: false,
    })
  }

  // Calculate how many sections are visible
  const visibleCount = Object.values(visibility).filter(Boolean).length
  const totalCount = sections.length

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t('sharing.privacyControls.title')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t('sharing.privacyControls.description')}
        </p>
      </div>

      {/* Quick actions */}
      <div className="flex gap-2">
        <button onClick={handleShowAll} className="btn-secondary text-sm">
          {t('sharing.privacyControls.showAll')}
        </button>
        <button onClick={handleHideAll} className="btn-secondary text-sm">
          {t('sharing.privacyControls.hideAll')}
        </button>
      </div>

      {/* Profile completeness indicator */}
      <div className="p-3 bg-blue-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            {t('sharing.privacyControls.visibleSections')}
          </span>
          <span className="text-sm font-semibold text-primary-600">
            {visibleCount}/{totalCount}
          </span>
        </div>
        <div className="w-full bg-white rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-primary-500 transition-all duration-300"
            style={{ width: `${(visibleCount / totalCount) * 100}%` }}
          />
        </div>
      </div>

      {/* Warning if too few sections visible */}
      {visibleCount < 3 && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800">
              {t('sharing.privacyControls.limitedVisibilityTitle')}
            </p>
            <p className="text-sm text-yellow-700">
              {t('sharing.privacyControls.limitedVisibilityMessage')}
            </p>
          </div>
        </div>
      )}

      {/* Section toggles */}
      <div className="space-y-3">
        {sections.map((section) => {
          const isVisible = visibility[section.key]
          const label = t(`sharing.privacyControls.sections.${section.labelKey}.label`)
          const description = t(`sharing.privacyControls.sections.${section.labelKey}.description`)

          return (
            <div
              key={section.key}
              className="flex items-start justify-between p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start gap-3 flex-grow">
                {isVisible ? (
                  <Eye className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <EyeOff className="w-5 h-5 text-gray-400 mt-0.5" />
                )}
                <div>
                  <p className="font-medium text-gray-900">{label}</p>
                  <p className="text-sm text-gray-600">{description}</p>
                </div>
              </div>

              {/* Toggle switch */}
              <button
                onClick={() => handleToggle(section.key)}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                  ${isVisible ? 'bg-primary-600' : 'bg-gray-200'}
                `}
                role="switch"
                aria-checked={isVisible}
                aria-label={`Toggle ${label}`}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                    ${isVisible ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
